import { FC } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue, useSetRecoilState } from "recoil";

import { PIC, SVG_MUI } from "@ryde/assets";
import { BulkActionsPanel, IBulkActionItem } from "@ryde/controls";
import { BaseDrawer, DrawerContentStyled, DrawerHeader } from "@ryde/layout";
import { useCoursesBulkActions, useModalConfirm } from "@ryde/redux";
import {
  ActionsEnum,
  bulkOperationsDrawerAF,
  ConfirmResult,
  RydeUiUnit,
  selectedCoursesCalendarSlotIdsAtom,
  selectedCoursesCalendarSlotsSelector,
} from "@ryde/shared";

import { downloadCoursesCalendarSlotsCsv } from "./courses-calendar-csv";

const entity: RydeUiUnit = "course";

export const CoursesBulkOperationsCalendarDrawer: FC = () => {
  const { t } = useTranslation();
  const selectedSlots = useRecoilValue(selectedCoursesCalendarSlotsSelector);
  const setBulkOperationsDrawerOpened = useSetRecoilState(
    bulkOperationsDrawerAF(entity),
  );
  const setSelectedSlotIds = useSetRecoilState(
    selectedCoursesCalendarSlotIdsAtom,
  );
  const { confirmDialogResult } = useModalConfirm();
  const { bulkDeepRecalculateCourses } = useCoursesBulkActions();

  const onCloseDrawer = useEvent(() => setBulkOperationsDrawerOpened(false));

  const handleExportCsvClick = useEvent((): void => {
    downloadCoursesCalendarSlotsCsv(selectedSlots);
    onCloseDrawer();
  });

  const handleDeepRecalculateClick = useEvent(async (): Promise<void> => {
    const courseIds = [...new Set(selectedSlots.map(slot => slot.courseId))];

    if (!courseIds.length) {
      return;
    }

    const confirmationResult = await confirmDialogResult({
      title: t("general.CONFIRM"),
      message: t("general.DEEP_RECALCULATE_MESSAGE"),
    });

    if (confirmationResult !== ConfirmResult.OK) {
      return;
    }

    const result = await bulkDeepRecalculateCourses(courseIds);

    if (result?.success) {
      setSelectedSlotIds(new Set());
      onCloseDrawer();
    }
  });

  const actions: IBulkActionItem[] = [
    {
      key: "exportCsv",
      label: t("general.EXPORT_CSV"),
      hint: t("course.bulk.hints.exportCsv"),
      icon: PIC.DownloadSVG,
      onClick: handleExportCsvClick,
    },
    {
      key: "deepRecalculate",
      label: t("general.DEEP_RECALCULATE"),
      hint: t("course.bulk.hints.deepRecalculate"),
      icon: SVG_MUI.ArrowsRotate3SVG,
      permission: { entity, action: ActionsEnum.Update },
      onClick: () => void handleDeepRecalculateClick(),
    },
  ];

  return (
    <BaseDrawer onCloseDrawer={onCloseDrawer}>
      <DrawerHeader
        title={t("general.BULK_ACTIONS")}
        onCloseDrawer={onCloseDrawer}
      />
      <DrawerContentStyled className="bulk-action-drawer">
        <BulkActionsPanel actions={actions} />
      </DrawerContentStyled>
    </BaseDrawer>
  );
};
